
import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Clock, File, Calendar, Play, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';

interface RecordingDetails {
  id: string;
  streamId: number;
  title: string;
  duration: number;
  fileSize: number;
  createdAt: string;
  status: 'processing' | 'ready' | 'failed';
  downloadUrl?: string;
  metadata?: {
    resolution?: string;
    bitrate?: string;
    format?: string;
    codec?: string;
  };
}

interface StreamRecordingDetailsProps {
  streamId: number;
  recordingId: string;
}

export function StreamRecordingDetails({ streamId, recordingId }: StreamRecordingDetailsProps) {
  const [recording, setRecording] = useState<RecordingDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchRecordingDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ recording: RecordingDetails }>(`api/streams/${streamId}/recordings/${recordingId}`);

      if (response.success && response.data) {
        setRecording(response.data.recording);
      } else {
        toast.error(response.error?.message || 'Failed to load recording details');
        console.error('Fetch recording details error:', response.error);
      }
    } catch (error) {
      toast.error('Failed to load recording details');
      console.error('Fetch recording details error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [streamId, recordingId]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (recordingId) {
      fetchRecordingDetails();
    }
  }, [streamId, recordingId, fetchRecordingDetails]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading recording details...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded animate-pulse" />
            <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
            <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!recording) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Recording details not available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <File className="h-5 w-5" />
              Recording Details
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Metadata and information for this recording
            </p>
          </div>
          <Badge variant={
            recording.status === 'ready' ? 'default' : 
            recording.status === 'processing' ? 'secondary' : 'destructive'
          }>
            {recording.status}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Title</h4>
            <p className="font-medium">{recording.title}</p>
          </div>
          
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Duration</h4>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{formatDuration(recording.duration)}</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">File Size</h4>
            <div className="flex items-center gap-2">
              <File className="h-4 w-4 text-muted-foreground" />
              <span>{formatFileSize(recording.fileSize)}</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Created</h4>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{new Date(recording.createdAt).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Metadata */}
        {recording.metadata && (
          <div className="border-t pt-6">
            <h4 className="text-sm font-medium text-muted-foreground mb-4">Technical Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recording.metadata.resolution && (
                <div className="space-y-2">
                  <h5 className="text-xs text-muted-foreground">Resolution</h5>
                  <p className="font-mono text-sm">{recording.metadata.resolution}</p>
                </div>
              )}
              
              {recording.metadata.bitrate && (
                <div className="space-y-2">
                  <h5 className="text-xs text-muted-foreground">Bitrate</h5>
                  <p className="font-mono text-sm">{recording.metadata.bitrate}</p>
                </div>
              )}
              
              {recording.metadata.format && (
                <div className="space-y-2">
                  <h5 className="text-xs text-muted-foreground">Format</h5>
                  <p className="font-mono text-sm">{recording.metadata.format}</p>
                </div>
              )}
              
              {recording.metadata.codec && (
                <div className="space-y-2">
                  <h5 className="text-xs text-muted-foreground">Codec</h5>
                  <p className="font-mono text-sm">{recording.metadata.codec}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Recording ID: {recording.id}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => window.open(recording.downloadUrl || '#', '_blank')}
            disabled={!recording.downloadUrl || recording.status !== 'ready'}
          >
            <Download className="h-4 w-4" />
          </Button>
          {recording.downloadUrl && recording.status === 'ready' && (
            <Button
              onClick={() => window.open(recording.downloadUrl, '_blank')}
            >
              <Play className="h-4 w-4 mr-2" />
              Preview
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
